import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { logoutUser } from "../api/auth";
import { useAdminStore } from "../context/AdminContext";

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedAdmin } = useAdminStore();

  const [leftMenuOpen, setLeftMenuOpen] = useState(false);
  const [userDropOpen, setUserDropOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 991 : false
  );

  const leftMenuRef = useRef(null);
  const userRef = useRef(null);

  const adminName = selectedAdmin?.adminName || "Admin";
  const adminEmail = selectedAdmin?.adminEmail || "admin@gmail.com";

  const handleLogout = () => {
    logoutUser();
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  };

  /* RESPONSIVE WIDTH */
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 991;
      setShowNavbar(isMobile);

      if (!isMobile) {
        setLeftMenuOpen(false);
        setUserDropOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  /* CLOSE ON ROUTE CHANGE */
  useEffect(() => {
    setLeftMenuOpen(false);
    setUserDropOpen(false);
  }, [location.pathname]);

  /* OUTSIDE CLICK */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (leftMenuRef.current && !leftMenuRef.current.contains(e.target)) {
        setLeftMenuOpen(false);
      }
      if (userRef.current && !userRef.current.contains(e.target)) {
        setUserDropOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  if (!showNavbar) return null;

  return (
    <>
      <nav className="mobile-navbar">
        {/* LEFT SIDE */}
        <div className="mobile-left-box" ref={leftMenuRef}>
          <button
            className={`mobile-menu-btn ${leftMenuOpen ? 'active' : ''}`}
            aria-label="Open menu"
            onClick={() => {
              setLeftMenuOpen((prev) => !prev);
              setUserDropOpen(false);
            }}
          >
            <span className="menu-line line1"></span>
            <span className="menu-line line2"></span>
            <span className="menu-line line3"></span>
          </button>

          <span className="mobile-brand">Admin Panel</span>

          <div className={`mobile-left-dropdown ${leftMenuOpen ? 'show' : ''}`}>
            <div className="dropdown-content">
              <div className="dropdown-header">
                <i className="fas fa-sliders-h"></i>
                <span>Navigation Menu</span>
              </div>
              
              <div className="menu-section">
                <div className="section-label">Dashboard</div>
                <NavLink to="/admindashboard" className="mobile-menu-item">
                  <i className="fas fa-tachometer-alt"></i>
                  <span>Dashboard</span>
                </NavLink>
              </div>

              <div className="menu-section">
                <div className="section-label">Notifications</div>
                <NavLink to="/notify" className="mobile-menu-item">
                  <i className="fas fa-bell"></i>
                  <span>Notifications</span>
                </NavLink>
              </div>

              <div className="menu-section">
                <div className="section-label">Manage Forms</div>
                <NavLink to="/userform" className="mobile-menu-item">
                  <i className="fas fa-user-plus"></i>
                  <span>Manager Registration</span>
                </NavLink>
                <NavLink to="/vipform" className="mobile-menu-item">
                  <i className="fas fa-user-tie"></i>
                  <span>VIP Registration</span>
                </NavLink>
                <NavLink to="/guardform" className="mobile-menu-item">
                  <i className="fas fa-shield-alt"></i>
                  <span>Guard Registration</span>
                </NavLink>
              </div>

              <div className="menu-section">
                <div className="section-label">Tables</div>
                <NavLink to="/viplist" className="mobile-menu-item">
                  <i className="fas fa-table"></i>
                  <span>VIP Table</span>
                </NavLink>
                <NavLink to="/guardlist" className="mobile-menu-item">
                  <i className="fas fa-table"></i>
                  <span>Guard Table</span>
                </NavLink>
                <NavLink to="/selfregistrationcheck" className="mobile-menu-item">
                  <i className="fas fa-user-check"></i>
                  <span>Activate Users</span>
                </NavLink>
              </div>

              <div className="menu-section">
                <div className="section-label">Duty Management</div>
                <NavLink to="/adminrequestaccept" className="mobile-menu-item">
                  <i className="fas fa-tasks"></i>
                  <span>Duty Accept/Reject</span>
                </NavLink>
                <NavLink to="/dutyhistory" className="mobile-menu-item">
                  <i className="fas fa-history"></i>
                  <span>Guard Duty History</span>
                </NavLink>
              </div>

              <div className="menu-section">
                <div className="section-label">History</div>
                <NavLink to="/updatehistory" className="mobile-menu-item">
                  <i className="fas fa-code-branch"></i>
                  <span>Updation History</span>
                </NavLink>
              </div>

              <div className="menu-section">
                <div className="section-label">Incidents</div>
                <NavLink to="/incidents" className="mobile-menu-item">
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>Incidents</span>
                </NavLink>
              </div>

              <div className="menu-section">
                {/* <div className="section-label">Settings</div> */}
                {/* <NavLink to="/setting" className="mobile-menu-item">
                  <i className="fas fa-cog"></i>
                  <span>Settings</span>
                </NavLink> */}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="mobile-user-dropdown-box" ref={userRef}>
          <button
            className={`mobile-profile-btn ${userDropOpen ? 'active' : ''}`}
            aria-label="Open profile menu"
            onClick={() => {
              setUserDropOpen((prev) => !prev);
              setLeftMenuOpen(false);
            }}
          >
            <div className="profile-avatar">
              <img
                src="/assets/img/avtar.jpg"
                alt="profile"
                className="mobile-avatar"
              />
              <div className="online-indicator"></div>
            </div>
          </button>

          <div className={`mobile-user-dropdown ${userDropOpen ? 'show' : ''}`}>
            <div className="user-dropdown-header">
              <div className="user-avatar-large">
                <img
                  src="/assets/img/avtar.jpg"
                  alt="profile"
                />
              </div>
              <div className="user-info">
                <h4 className="user-name">{adminName}</h4>
                <p className="user-email">{adminEmail}</p>
                <div className="user-status">
                  <span className="status-dot"></span>
                  <span className="status-text">Online</span>
                </div>
              </div>
            </div>

            <div className="dropdown-divider"></div>

            <NavLink to="/adminprofile" className="user-dropdown-item">
              <i className="fas fa-user-circle"></i>
              <span>My Profile</span>
              <i className="fas fa-chevron-right"></i>
            </NavLink>

            {/* <NavLink to="/setting" className="user-dropdown-item">
              <i className="fas fa-cog"></i>
              <span>Settings</span>
              <i className="fas fa-chevron-right"></i>
            </NavLink> */}

            <div className="dropdown-divider"></div>

            <button onClick={handleLogout} className="mobile-logout-btn">
              <i className="fas fa-sign-out-alt"></i>
              <span>Logout</span>
            </button>
          </div>
        </div>
      </nav>

      <style jsx>{`
        .mobile-navbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.8rem 1.2rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 9999;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }

        .mobile-left-box {
          display: flex;
          align-items: center;
          gap: 1rem;
          position: relative;
          flex: 1;
        }

        .mobile-menu-btn {
          width: 40px;
          height: 40px;
          background: rgba(255, 255, 255, 0.1);
          border: 2px solid rgba(255, 255, 255, 0.2);
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 5px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          padding: 8px;
        }

        .mobile-menu-btn:hover {
          background: rgba(255, 255, 255, 0.15);
          transform: scale(1.05);
          border-color: rgba(255, 255, 255, 0.3);
        }

        .mobile-menu-btn.active {
          background: rgba(255, 255, 255, 0.2);
        }

        .menu-line {
          width: 20px;
          height: 2px;
          background: white;
          border-radius: 2px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: block;
        }

        .mobile-menu-btn.active .line1 {
          transform: rotate(45deg) translate(5px, 5px);
        }

        .mobile-menu-btn.active .line2 {
          opacity: 0;
        }

        .mobile-menu-btn.active .line3 {
          transform: rotate(-45deg) translate(7px, -6px);
        }

        .mobile-brand {
          font-size: 1.1rem;
          font-weight: 700;
          color: white;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          letter-spacing: 0.5px;
        }

        .mobile-left-dropdown {
          position: fixed;
          top: 60px;
          left: 0;
          width: 280px;
          max-width: 85vw;
          height: calc(100vh - 60px);
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
          z-index: 9998;
          transform: translateX(-100%);
          transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          overflow-y: auto;
          opacity: 0;
          visibility: hidden;
        }

        .mobile-left-dropdown.show {
          transform: translateX(0);
          opacity: 1;
          visibility: visible;
        }

        .dropdown-content {
          padding: 1rem 0;
        }

        .dropdown-header {
          padding: 1.2rem 1.5rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          align-items: center;
          gap: 0.8rem;
          margin-bottom: 0.5rem;
        }

        .dropdown-header i {
          color: #667eea;
          font-size: 1.2rem;
        }

        .dropdown-header span {
          color: white;
          font-weight: 600;
          font-size: 1rem;
          letter-spacing: 0.5px;
        }

        .menu-section {
          padding: 0.5rem 0;
        }

        .section-label {
          color: rgba(255, 255, 255, 0.5);
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          padding: 0.5rem 1.5rem;
          margin-top: 0.5rem;
        }

        .mobile-menu-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.9rem 1.5rem;
          color: rgba(255, 255, 255, 0.8);
          text-decoration: none;
          transition: all 0.3s ease;
          border-left: 3px solid transparent;
          font-size: 0.9rem;
        }

        .mobile-menu-item:hover {
          background: rgba(255, 255, 255, 0.05);
          color: white;
          border-left: 3px solid #667eea;
        }

        .mobile-menu-item.active {
          background: rgba(102, 126, 234, 0.1);
          color: #667eea;
          border-left: 3px solid #667eea;
        }

        .mobile-menu-item i {
          width: 20px;
          text-align: center;
          font-size: 1rem;
        }

        .mobile-user-dropdown-box {
          position: relative;
          flex-shrink: 0;
        }

        .mobile-profile-btn {
          background: rgba(255, 255, 255, 0.1);
          border: 2px solid rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          width: 40px;
          height: 40px;
          padding: 0;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .mobile-profile-btn:hover {
          background: rgba(255, 255, 255, 0.15);
          transform: scale(1.05);
          border-color: rgba(255, 255, 255, 0.3);
        }

        .mobile-profile-btn.active {
          background: rgba(255, 255, 255, 0.2);
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.3);
        }

        .profile-avatar {
          position: relative;
          width: 100%;
          height: 100%;
        }

        .mobile-avatar {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
        }

        .online-indicator {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 10px;
          height: 10px;
          background: #10b981;
          border: 2px solid #1a1a2e;
          border-radius: 50%;
        }

        .mobile-user-dropdown {
          position: absolute;
          top: 55px;
          right: 0;
          width: 300px;
          max-width: 90vw;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-radius: 16px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
          overflow: hidden;
          z-index: 9999;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-10px);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .mobile-user-dropdown.show {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .user-dropdown-header {
          padding: 1.5rem;
          background: rgba(255, 255, 255, 0.05);
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .user-avatar-large {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          overflow: hidden;
          border: 3px solid #667eea;
        }

        .user-avatar-large img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .user-info {
          flex: 1;
        }

        .user-name {
          color: white;
          font-size: 1.1rem;
          font-weight: 600;
          margin: 0 0 0.2rem 0;
        }

        .user-email {
          color: rgba(255, 255, 255, 0.6);
          font-size: 0.85rem;
          margin: 0 0 0.5rem 0;
        }

        .user-status {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .status-dot {
          width: 8px;
          height: 8px;
          background: #10b981;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }

        .status-text {
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.8rem;
        }

        .dropdown-divider {
          height: 1px;
          background: rgba(255, 255, 255, 0.1);
          margin: 0.5rem 0;
        }

        .user-dropdown-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem 1.5rem;
          color: rgba(255, 255, 255, 0.8);
          text-decoration: none;
          transition: all 0.3s ease;
          font-size: 0.9rem;
        }

        .user-dropdown-item:hover {
          background: rgba(255, 255, 255, 0.05);
          color: white;
          padding-left: 1.8rem;
        }

        .user-dropdown-item i:first-child {
          margin-right: 1rem;
          color: #667eea;
          width: 20px;
          text-align: center;
        }

        .user-dropdown-item i:last-child {
          color: rgba(255, 255, 255, 0.4);
          font-size: 0.8rem;
        }

        .mobile-logout-btn {
          width: 100%;
          background: rgba(220, 53, 69, 0.9);
          border: none;
          color: white;
          padding: 1rem 1.5rem;
          font-weight: 600;
          cursor: pointer;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.8rem;
          transition: all 0.3s ease;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .mobile-logout-btn:hover {
          background: #dc3545;
          gap: 1rem;
        }

        /* Custom scrollbar */
        .mobile-left-dropdown::-webkit-scrollbar {
          width: 6px;
        }

        .mobile-left-dropdown::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
        }

        .mobile-left-dropdown::-webkit-scrollbar-thumb {
          background: rgba(102, 126, 234, 0.5);
          border-radius: 10px;
        }

        .mobile-left-dropdown::-webkit-scrollbar-thumb:hover {
          background: rgba(102, 126, 234, 0.8);
        }

        /* Responsive adjustments */
        @media (max-width: 480px) {
          .mobile-navbar {
            padding: 0.7rem 1rem;
          }
          
          .mobile-left-dropdown {
            width: 260px;
          }
          
          .mobile-user-dropdown {
            width: 280px;
            max-width: calc(100vw - 20px);
          }
          
          .mobile-brand {
            font-size: 1rem;
          }
        }

        @media (max-width: 360px) {
          .mobile-left-dropdown {
            width: 240px;
          }
          
          .mobile-user-dropdown {
            width: 260px;
          }
        }
      `}</style>
    </>
  );
}
// import React, { useState, useEffect, useRef } from "react";
// import { NavLink, useNavigate, useLocation } from "react-router-dom";
// import { logoutUser } from "../api/auth";
// import { useAdminStore } from "../context/AdminContext";

// export default function Navbar() {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const { selectedAdmin } = useAdminStore();

//   const [leftMenuOpen, setLeftMenuOpen] = useState(false);
//   const [userDropOpen, setUserDropOpen] = useState(false);
// const [showNavbar, setShowNavbar] = useState(
//   typeof window !== "undefined" ? window.innerWidth <= 991 : false
// );

//   const leftMenuRef = useRef(null);
//   const userRef = useRef(null);

//   const adminName = selectedAdmin?.adminName || "Admin";
//   const adminEmail = selectedAdmin?.adminEmail || "admin@gmail.com";

//   const handleLogout = () => {
//     logoutUser();
//     localStorage.clear();
//     sessionStorage.clear();
//     navigate("/login");
//   };

//   /* RESPONSIVE WIDTH */
//   useEffect(() => {
//     const handleResize = () => {
//       const isMobile = window.innerWidth <= 991;
//       setShowNavbar(isMobile);

//       if (!isMobile) {
//         setLeftMenuOpen(false);
//         setUserDropOpen(false);
//       }
//     };

//     handleResize();
//     window.addEventListener("resize", handleResize);
//     window.addEventListener("orientationchange", handleResize);

//     return () => {
//       window.removeEventListener("resize", handleResize);
//       window.removeEventListener("orientationchange", handleResize);
//     };
//   }, []);

//   /* CLOSE ON ROUTE CHANGE */
//   useEffect(() => {
//     setLeftMenuOpen(false);
//     setUserDropOpen(false);
//   }, [location.pathname]);

//   /* OUTSIDE CLICK */
//   useEffect(() => {
//     const handleClickOutside = (e) => {
//       if (leftMenuRef.current && !leftMenuRef.current.contains(e.target)) {
//         setLeftMenuOpen(false);
//       }
//       if (userRef.current && !userRef.current.contains(e.target)) {
//         setUserDropOpen(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     document.addEventListener("touchstart", handleClickOutside);

//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//       document.removeEventListener("touchstart", handleClickOutside);
//     };
//   }, []);

//   if (!showNavbar) return null;

//   return (
//     <nav style={styles.navbar}>
//       {/* LEFT SIDE */}
//       <div style={styles.leftBox} ref={leftMenuRef}>
//         <button
//           style={styles.dashBtn}
//           aria-label="Open menu"
//           onClick={() => {
//             setLeftMenuOpen((prev) => !prev);
//             setUserDropOpen(false);
//           }}
//         >
//           ☰
//         </button>

//         <span style={styles.brand}>Admin Panel</span>

//         {leftMenuOpen && (
//           <div style={styles.leftDropdown}>
//             <NavLink to="/admindashboard" style={styles.menuItem}>Dashboard</NavLink>
//             <NavLink to="/notify" style={styles.menuItem}>Notifications</NavLink>
//             <NavLink to="/userform" style={styles.menuItem}>Manager Registration Form</NavLink>
//             <NavLink to="/vipform" style={styles.menuItem}>VIP Registration Form</NavLink>
//             <NavLink to="/guardform" style={styles.menuItem}>Guard Registration Form</NavLink>
//             {/* <NavLink to="/designation" style={styles.menuItem}>Add Designations</NavLink> */}
//             {/* <NavLink to="/rank" style={styles.menuItem}>Add Ranks</NavLink> */}
//             <NavLink to="/viplist" style={styles.menuItem}>VIP Table</NavLink>
//             <NavLink to="/guardlist" style={styles.menuItem}>Guard Table</NavLink>
//             <NavLink to="/selfregistrationcheck" style={styles.menuItem}>Activate Users</NavLink>
//             <NavLink to="/adminrequestaccept" style={styles.menuItem}>Duty Accept/Reject</NavLink>
//             <NavLink to="/dutyhistory" style={styles.menuItem}>Guard-duty-history</NavLink>
//             <NavLink to="/updatehistory" style={styles.menuItem}>Updation history</NavLink>
//             <NavLink to="/incidents" style={styles.menuItem}>Incidents</NavLink>
//              <NavLink to="/setting" style={styles.menuItem}>Settings</NavLink>
//           </div>
//         )}
//       </div>

//       {/* RIGHT SIDE */}
//       <div style={styles.userDropdownBox} ref={userRef}>
//         <button
//           style={styles.profileIconBtn}
//           aria-label="Open profile menu"
//           onClick={() => {
//             setUserDropOpen((prev) => !prev);
//             setLeftMenuOpen(false);
//           }}
//         >
//           <img
//             src="/assets/img/avtar.jpg"
//             alt="profile"
//             style={styles.avatar}
//           />
//         </button>

//         {userDropOpen && (
//           <div style={styles.userDropdown}>
//             <div style={styles.userHeader}>
//               <img
//                 src="/assets/img/avtar.jpg"
//                 alt="profile"
//                 style={styles.userBigAvatar}
//               />
//               <div>
//                 <strong>{adminName}</strong>
//                 <br />
//                 <small>{adminEmail}</small>
//               </div>
//             </div>

//             <NavLink to="/adminprofile" style={styles.userItem}>
//               My Profile
//             </NavLink>

//             <button onClick={handleLogout} style={styles.logoutBtn}>
//               Logout
//             </button>
//           </div>
//         )}
//       </div>
//     </nav>
//   );
// }

// /*  RESPONSIVE STYLES ONLY */

// const styles = {
//   navbar: {
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "space-between",
//     padding: "10px 14px",
//     background: "#ffffff",
//     borderBottom: "1px solid #ddd",
//     position: "sticky",
//     top: 0,
//     zIndex: 1000,
//     width: "100%",
//   },

//   leftBox: {
//     display: "flex",
//     alignItems: "center",
//     gap: 10,
//     position: "relative",
//     maxWidth: "70%",
//     flexWrap: "nowrap",
//   },

//   dashBtn: {
//     fontSize: 20,
//     fontWeight: "900",
//     background: "none",
//     border: "2px solid #4e54c8",
//     borderRadius: 6,
//     padding: "2px 8px",
//     cursor: "pointer",
//     color: "#4e54c8",
//     minWidth: 36,
//     minHeight: 32,
//   },

//   brand: {
//     fontSize: 15,
//     fontWeight: "700",
//     color: "#4e54c8",
//     whiteSpace: "nowrap",
//   },

//   leftDropdown: {
//     position: "absolute",
//     top: "38px",
//     left: 0,
//     width: "240px",
//     maxHeight: "70vh",
//     overflowY: "auto",
//     background: "#fff",
//     borderRadius: 12,
//     boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
//     zIndex: 1000,
//   },

//   menuItem: {
//     display: "block",
//     padding: "12px 14px",
//     textDecoration: "none",
//     color: "#333",
//     borderBottom: "1px solid #eee",
//     fontWeight: 600,
//     fontSize: 14,
//   },

//   userDropdownBox: {
//     position: "relative",
//     flexShrink: 0,
//   },

//   profileIconBtn: {
//     background: "none",
//     border: "none",
//     padding: 0,
//     cursor: "pointer",
//   },

//   avatar: {
//     width: 32,
//     height: 32,
//     borderRadius: "50%",
//     border: "2px solid #4e54c8",
//     objectFit: "cover",
//   },

//   userDropdown: {
//     position: "absolute",
//     right: 0,
//     top: "40px",
//     width: "220px",
//     maxWidth: "90vw",
//     background: "#fff",
//     borderRadius: 12,
//     boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
//     overflow: "hidden",
//     zIndex: 1000,
//   },

//   userHeader: {
//     display: "flex",
//     gap: 10,
//     padding: 12,
//     background: "#f8f9fa",
//     borderBottom: "1px solid #eee",
//     alignItems: "center",
//   },

//   userBigAvatar: {
//     width: 38,
//     height: 38,
//     borderRadius: "50%",
//   },

//   userItem: {
//     display: "block",
//     padding: "10px 14px",
//     textDecoration: "none",
//     color: "#333",
//     fontWeight: 600,
//     fontSize: 14,
//   },

//   logoutBtn: {
//     width: "100%",
//     border: "none",
//     background: "#dc3545",
//     color: "#fff",
//     padding: 12,
//     fontWeight: 700,
//     cursor: "pointer",
//     fontSize: 14,
//   },
// };
